import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Container, Row, Col } from 'react-bootstrap';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  Volume2,
  VolumeX,
  User,
  Timer,
  Wifi,
  Cloud,
  CloudOff
} from 'lucide-react';
import { getMediaSocket } from '../../utils/chat/telemedicineSockets';
import { parseAvatar } from '../../utils/chat/utils';

// Call Quality enum
const CallQuality = {
  EXCELLENT: 'excellent',
  GOOD: 'good',
  POOR: 'poor',
  VERY_POOR: 'very_poor'
};

// Call Mode enum
const CallMode = {
  VIDEO: 'video',
  AUDIO_ONLY: 'audio_only'
};

// Fetch TURN credentials from the media server (centralized, no hardcoded secrets)
const MEDIA_SERVER_URL =
  process.env.REACT_APP_MEDIA_SERVER_URL || "https://ai.dedahospital.com";

const TurnService = {
  getTurnCredentials: async () => {
    try {
      const response = await fetch(
        `${MEDIA_SERVER_URL}/media/turn-credentials`,
        { method: "GET" },
      );
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching TURN credentials from server:", error);
      throw error;
    }
  },
};

const CallScreen = ({ callConfig }) => {
  const { name, roomId, patientId, peerId, onEndCall, audioOnly, callerAvatar } = callConfig;
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const socketRef = useRef(null);

  // Call State
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [callMode, setCallMode] = useState(audioOnly ? CallMode.AUDIO_ONLY : CallMode.VIDEO);
  const [networkQuality, setNetworkQuality] = useState(CallQuality.GOOD);

  // Call Timer
  const [callDuration, setCallDuration] = useState(0);
  const callStartTimeRef = useRef(null);
  const timerRef = useRef(null);
  const qualityTimerRef = useRef(null);

  // Network Quality Monitoring
  const [autoSwitchedToAudio, setAutoSwitchedToAudio] = useState(false);
  const [usingTwilioTurn, setUsingTwilioTurn] = useState(false);

  const cleanupRef = useRef(false);
  const setupInProgressRef = useRef(false);

  // ICE configuration
  const iceConfigurationRef = useRef({
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
    iceCandidatePoolSize: 10,
    iceTransportPolicy: 'all',
  });

  // Format duration
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  // Setup ICE configuration
  const setupIceConfiguration = useCallback(async () => {
    try {
      const result = await TurnService.getTurnCredentials();
      const iceServers = result.iceServers.map(server => {
        const urls = server.urls;
        const username = server.username;
        const credential = server.credential;

        if (urls.startsWith('stun:')) {
          return { urls };
        }
        else if (urls.startsWith('turn:') || urls.startsWith('turns:')) {
          if (username != null && credential != null) {
            return { urls, username, credential };
          }
          return null;
        }
        return { urls };
      }).filter(server => server !== null);

      iceConfigurationRef.current = {
        iceServers,
        iceCandidatePoolSize: 10,
        iceTransportPolicy: 'all',
      };

      setUsingTwilioTurn(result.provider === 'twilio');
    } catch (error) {
      iceConfigurationRef.current = {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          {
            urls: 'turn:openrelay.metered.ca:80',
            username: 'openrelay.project',
            credential: 'openrelay',
          },
        ],
        iceCandidatePoolSize: 10,
        iceTransportPolicy: 'all',
      };

      setUsingTwilioTurn(false);
    }
  }, []);

  // Start quality monitoring
  const startQualityMonitoring = useCallback(() => {
    qualityTimerRef.current = setInterval(async () => {
      if (peerConnectionRef.current && isConnected) {
        await checkNetworkQuality();
      }
    }, 2000);
  }, [isConnected]);

  // Check network quality
  const checkNetworkQuality = useCallback(async () => {
    try {
      const stats = await peerConnectionRef.current.getStats();

      for (const report of stats.values()) {
        if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
          const packetsLost = report.packetsLost || 0;
          const packetsReceived = report.packetsReceived || 0;

          if (packetsReceived > 0) {
            const lossRate = packetsLost / (packetsLost + packetsReceived);
            updateNetworkQuality(lossRate);

            if (lossRate > 0.15 && callMode === CallMode.VIDEO && !autoSwitchedToAudio) {
              switchToAudioOnly(true);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking network quality:', error);
    }
  }, [callMode, autoSwitchedToAudio]);

  // Update network quality
  const updateNetworkQuality = useCallback((lossRate) => {
    let newQuality;
    if (lossRate < 0.02) {
      newQuality = CallQuality.EXCELLENT;
    } else if (lossRate < 0.05) {
      newQuality = CallQuality.GOOD;
    } else if (lossRate < 0.15) {
      newQuality = CallQuality.POOR;
    } else {
      newQuality = CallQuality.VERY_POOR;
    }

    if (newQuality !== networkQuality) {
      setNetworkQuality(newQuality);
    }
  }, [networkQuality]);

  // Handle connection failure
  const handleConnectionFailure = useCallback(() => {
    // Handle connection failure logic
  }, []);

  // Switch to audio only
  const switchToAudioOnly = useCallback(async (auto = false) => {
    if (callMode === CallMode.AUDIO_ONLY) return;

    setCallMode(CallMode.AUDIO_ONLY);
    setAutoSwitchedToAudio(auto);

    // Remove video tracks safely
    if (peerConnectionRef.current) {
      try {
        const senders = peerConnectionRef.current.getSenders();
        const videoTracks = localStreamRef.current?.getVideoTracks() || [];

        for (const track of videoTracks) {
          track.stop();

          const sender = senders.find(sender => sender.track && sender.track.id === track.id);
          if (sender) {
            await peerConnectionRef.current.removeTrack(sender);
          }
        }
      } catch (error) {
        console.error('Error removing video tracks:', error);
      }
    }
  }, [callMode]);

  // Switch to video
  const switchToVideo = useCallback(async () => {
    if (callMode === CallMode.VIDEO) return;

    try {
      // Get new video stream
      const videoStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          width: { min: 320, max: 640 },
          height: { min: 240, max: 480 },
        },
      });

      // Add video track to existing audio stream
      const videoTrack = videoStream.getVideoTracks()[0];
      localStreamRef.current.addTrack(videoTrack);

      if (peerConnectionRef.current) {
        peerConnectionRef.current.addTrack(videoTrack, localStreamRef.current);
      }

      setCallMode(CallMode.VIDEO);
      setAutoSwitchedToAudio(false);
      setIsCameraOff(false); // Ensure camera is on when switching to video

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
    } catch (error) {
      console.error('Failed to enable video:', error);
    }
  }, [callMode]);

  // Start call timer
  const startCallTimer = useCallback(() => {
    callStartTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
      }
    }, 1000);
  }, []);

  // Enable/disable an outgoing track of a given kind. We toggle the track on the
  // RTCRtpSender — that is the source of truth for what the remote (EMR) actually
  // receives — AND the localStreamRef track (for the local preview). Toggling
  // only localStreamRef is not enough: it can become desynced from the sender's
  // track, so muting it wouldn't stop the stream the EMR sees.
  const setOutgoingTrackEnabled = useCallback((kind, enabled) => {
    const pc = peerConnectionRef.current;
    const senderTrack = pc
      ?.getSenders()
      .find((s) => s.track && s.track.kind === kind)?.track;
    const localTrack = localStreamRef.current
      ?.getTracks()
      .find((t) => t.kind === kind);

    if (senderTrack) senderTrack.enabled = enabled;
    if (localTrack && localTrack !== senderTrack) localTrack.enabled = enabled;

    return senderTrack || localTrack || null;
  }, []);

  const currentTrackEnabled = useCallback((kind) => {
    const pc = peerConnectionRef.current;
    const senderTrack = pc
      ?.getSenders()
      .find((s) => s.track && s.track.kind === kind)?.track;
    const localTrack = localStreamRef.current
      ?.getTracks()
      .find((t) => t.kind === kind);
    const track = senderTrack || localTrack;
    return track ? track.enabled : true;
  }, []);

  // Toggle microphone
  const toggleMic = useCallback(() => {
    const newEnabled = !currentTrackEnabled('audio');
    const track = setOutgoingTrackEnabled('audio', newEnabled);
    if (track) setIsMicMuted(!newEnabled);
  }, [currentTrackEnabled, setOutgoingTrackEnabled]);

  // Toggle camera. Mutes/unmutes the outgoing video at the sender (the EMR sees
  // black frames when off) with no renegotiation — the reliable approach.
  const toggleCamera = useCallback(async () => {
    if (callMode === CallMode.AUDIO_ONLY) {
      // If in audio-only mode, switch to video mode
      await switchToVideo();
      return;
    }

    const newEnabled = !currentTrackEnabled('video');
    const track = setOutgoingTrackEnabled('video', newEnabled);
    if (track) setIsCameraOff(!newEnabled);
  }, [callMode, switchToVideo, currentTrackEnabled, setOutgoingTrackEnabled]);

  // Toggle speaker
  const toggleSpeaker = useCallback(() => {
    setIsSpeakerOn(!isSpeakerOn);
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = !isSpeakerOn;
    }
  }, [isSpeakerOn]);

  // Clean up socket listeners
  const cleanupSocketListeners = useCallback((socketService) => {
    socketService.off('offer');
    socketService.off('answer');
    socketService.off('ice-candidate');
    socketService.off('peer-disconnected');
    socketService.off('call-initiated');
    socketService.off('quality-update');
  }, []);

  // Setup WebRTC signaling
  const setupWebRTCSignaling = useCallback((socketService) => {
    socketService.on('offer', async (data) => {
      try {
        if (!peerConnectionRef.current) {
          await setupWebRTC();
        }

        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription({ sdp: data.offer.sdp, type: data.offer.type })
        );

        const answer = await peerConnectionRef.current.createAnswer();
        await peerConnectionRef.current.setLocalDescription(answer);

        socketService.emit('answer', {
          answer: answer.toMap ? answer.toMap() : { sdp: answer.sdp, type: answer.type },
          roomId,
          targetId: data.fromId,
        });
      } catch (error) {
        console.error('Error processing call offer:', error);
      }
    });

    socketService.on('answer', async (data) => {
      try {
        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription({ sdp: data.answer.sdp, type: data.answer.type })
        );
      } catch (error) {
        console.error('Failed to process call answer:', error);
      }
    });

    socketService.on('ice-candidate', async (data) => {
      try {
        if (peerConnectionRef.current) {
          await peerConnectionRef.current.addIceCandidate(
            new RTCIceCandidate({candidate:data.candidate, sdpMid:data.sdpMid, sdpMLineIndex:data.sdpMLineIndex})
          );
        }
      } catch (error) {
        console.error('Error adding ICE candidate:', error);
      }
    });

    socketService.on('peer-disconnected', () => {
      endCall();
    });

    socketService.on('quality-update', (data) => {
      if (data.suggestion === 'switch-to-audio') {
        switchToAudioOnly(true);
      }
    });
  }, [roomId, switchToAudioOnly]);

  // Setup WebRTC
  const setupWebRTC = useCallback(async () => {
    // Guard against React StrictMode's double-invoke (and any re-entry) creating
    // a second RTCPeerConnection / camera stream. setupInProgressRef is set
    // synchronously so a concurrent second call bails before acquiring media.
    if (peerConnectionRef.current || setupInProgressRef.current) return;
    setupInProgressRef.current = true;
    try {
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: callMode === CallMode.VIDEO ? {
          width: { min: 320, max: 640 },
          height: { min: 240, max: 480 },
        } : false,
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (error) {
        console.log('Video failed, falling back to audio-only:', error);
        setCallMode(CallMode.AUDIO_ONLY);
        setAutoSwitchedToAudio(true);
        constraints.video = false;
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      localStreamRef.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      peerConnectionRef.current = new RTCPeerConnection(iceConfigurationRef.current);

      // Add local stream tracks
      stream.getTracks().forEach(track => {
        track.enabled = true;
        peerConnectionRef.current.addTrack(track, stream);
      });

      // Handle remote stream
      peerConnectionRef.current.ontrack = (event) => {
        if (event.streams && event.streams.length > 0) {
          const remoteStream = event.streams[0];
          remoteStreamRef.current = remoteStream;
          
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
            remoteVideoRef.current.onloadedmetadata = () => {
              remoteVideoRef.current.play().catch(e => console.error('Play failed:', e));
            };
          }
          
          setIsConnected(true);
          startCallTimer();
          startQualityMonitoring();
        }
      };

      // Handle ICE candidates
      peerConnectionRef.current.onicecandidate = (event) => {
        if (event.candidate) {
          socketRef.current.emit('ice-candidate', {
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
            roomId,
          });
        }
      };

      // Handle connection state changes
      peerConnectionRef.current.onconnectionstatechange = () => {
        const state = peerConnectionRef.current.connectionState;
        switch (state) {
          case 'connected':
            setIsConnected(true);
            if (!callStartTimeRef.current) {
              startCallTimer();
            }
            break;
          case 'disconnected':
          case 'failed':
            handleConnectionFailure();
            break;
          default:
            break;
        }
      };

      peerConnectionRef.current.oniceconnectionstatechange = () => {
        const state = peerConnectionRef.current.iceConnectionState;
        switch (state) {
          case 'connected':
          case 'completed':
            setIsConnected(true);
            break;
          case 'disconnected':
          case 'failed':
            handleConnectionFailure();
            break;
          default:
            break;
        }
      };

      peerConnectionRef.current.onicegatheringstatechange = () => {
        if (peerConnectionRef.current.iceGatheringState === 'complete') {
          console.log('ICE gathering completed');
        }
      };
    } catch (error) {
      console.error('Failed to access microphone/camera:', error);
      endCall();
    } finally {
      setupInProgressRef.current = false;
    }
  }, [callMode, roomId, startCallTimer, startQualityMonitoring, handleConnectionFailure]);

  // Connect to socket
  const connectToSocket = useCallback(async () => {
        const token = localStorage.getItem('token');
    
        if (!token) {
          return;
        }
    
        // Get socket connection — ensure it's alive
        let s = getMediaSocket();
        if (!s || !s.connected) {
          // Socket disconnected; reconnect using stored credentials
          const uid = localStorage.getItem('userId') || patientId;
          const { connectMediaSocket } = await import('../../utils/chat/telemedicineSockets');
          connectMediaSocket(uid, token);
          s = getMediaSocket();
          // Wait for connection (up to 5s)
          if (s && !s.connected) {
            await new Promise((resolve) => {
              const onConnect = () => { s.off('connect', onConnect); resolve(); };
              s.on('connect', onConnect);
              setTimeout(resolve, 5000); // timeout fallback
            });
          }
        }
        socketRef.current = s;
    
        cleanupSocketListeners(socketRef.current);
        setupWebRTCSignaling(socketRef.current);
    
        socketRef.current.emit('join-room', {
          roomId,
          userId: patientId,
          userType: 'patient',
          isInitiator: false,
          token,
          roomType: 'call',
          audioOnly: audioOnly || false,
        }, (res) => {
          if (res && res.error) {
            console.log('Join room error:', res.error);
            return;
          }
          console.log('Joined room successfully');
        });
      }, [roomId, patientId, cleanupSocketListeners, setupWebRTCSignaling, audioOnly]);

  // Initialize socket and WebRTC
  const initSocketAndWebRTC = useCallback(async () => {
    await setupIceConfiguration();
    await setupWebRTC();
    await connectToSocket();
  }, [setupIceConfiguration, setupWebRTC, connectToSocket]);

  // Cleanup
  const cleanup = useCallback(() => {
    if (cleanupRef.current) return;
    cleanupRef.current = true;

    // Emit leave-room
    if (socketRef.current) {
      socketRef.current.emit('leave-room', {
        roomId,
        userId: patientId,
        roomType: 'call',
      });
    }

    // Stop all local stream tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach(track => track.stop());
    }

    // Clear renderers
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Cancel timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (qualityTimerRef.current) {
      clearInterval(qualityTimerRef.current);
    }

    // Cleanup socket listeners
    if (socketRef.current) {
      cleanupSocketListeners(socketRef.current);
    }
  }, [roomId, patientId, cleanupSocketListeners]);

  // End call. cleanup() must never prevent the UI from closing, so guard it and
  // always fire onEndCall (which dismisses the call modal).
  const endCall = useCallback(() => {
    try {
      cleanup();
    } catch (error) {
      console.error('Error during call cleanup:', error);
    }
    if (typeof onEndCall === 'function') {
      onEndCall();
    }
  }, [cleanup, onEndCall]);

  // Initialize on mount.
  // Re-arm the cleanup guard for this mount. React StrictMode double-invokes
  // this effect (run -> cleanup -> run), so the first cleanup() would otherwise
  // set cleanupRef.current = true permanently and poison the real end-call:
  // clicking "End" would then no-op (no leave-room emitted, tracks keep
  // streaming to the peer, RTCPeerConnection never closes). Empty deps keep it
  // to a single mount/unmount so callback-identity changes can't re-trigger it.
  useEffect(() => {
    cleanupRef.current = false;
    initSocketAndWebRTC();

    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get quality display values
  const getQualityColor = () => {
    switch (networkQuality) {
      case CallQuality.EXCELLENT:
        return '#28a745';
      case CallQuality.GOOD:
        return '#28a745';
      case CallQuality.POOR:
        return '#ffc107';
      case CallQuality.VERY_POOR:
        return '#dc3545';
      default:
        return '#28a745';
    }
  };

  const getQualityText = () => {
    switch (networkQuality) {
      case CallQuality.EXCELLENT:
        return 'Excellent';
      case CallQuality.GOOD:
        return 'Good';
      case CallQuality.POOR:
        return 'Poor';
      case CallQuality.VERY_POOR:
        return 'Very Poor';
      default:
        return 'Good';
    }
  };

  return (
    <Container fluid style={{ backgroundColor: '#000', height: '100vh', padding: 0 }}>
      <div style={{ position: 'relative', height: '100%' }}>
        {/* Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), rgba(0,0,0,1))'
        }} />

        {/* Remote video - always rendered (hidden in audio-only) so audio plays */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          controls={false}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            ...(callMode === CallMode.AUDIO_ONLY ? { opacity: 0, pointerEvents: 'none' } : {}),
          }}
        />

        {/* Audio-only avatar overlay */}
        {callMode === CallMode.AUDIO_ONLY && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
          }}>
            <img
              src={parseAvatar(callerAvatar)}
              alt=""
              style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid rgba(255,255,255,0.3)',
              }}
            />
            <h5 style={{ color: 'white', marginTop: 16, fontWeight: 600 }}>{name}</h5>
            <small style={{ color: 'rgba(255,255,255,0.7)' }}>Audio Call</small>
          </div>
        )}

        {/* Local video preview */}
        {callMode === CallMode.VIDEO && !isCameraOff && (
          <div style={{
            position: 'absolute',
            top: 100,
            right: 16,
            width: 120,
            height: 160,
            borderRadius: 12,
            border: '2px solid rgba(255,255,255,0.3)',
            overflow: 'hidden',
            zIndex: 2
          }}>
            {localStreamRef.current ? (
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'scaleX(-1)'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <User size={40} color="rgba(255,255,255,0.54)" />
              </div>
            )}
          </div>
        )}

        {/* Top bar with call info */}
        <div style={{
          position: 'absolute',
          top: 16,
          left: 16,
          right: 16,
          padding: 16,
          backgroundColor: 'rgba(0,0,0,0.6)',
          borderRadius: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 2
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 8,
              height: 8,
              marginRight: 8,
              backgroundColor: isConnected ? '#10b981' : '#f59e0b',
              borderRadius: '50%'
            }} />
            <div>
              <h6 style={{
                color: 'white',
                margin: 0,
                fontWeight: 600,
                fontSize: 16
              }}>
                {name}
              </h6>
              <small style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: 12
              }}>
                {isConnected ? 'Connected' : 'Connecting...'}
              </small>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Timer size={16} color="rgba(255,255,255,0.7)" style={{ marginRight: 4 }} />
            <span style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: 14,
              fontWeight: 500
            }}>
              {formatDuration(callDuration)}
            </span>
          </div>
        </div>

        {/* Quality indicator */}
        <div style={{
          position: 'absolute',
          top: 100,
          left: 16,
          zIndex: 2
        }}>
          <div style={{
            padding: '6px 12px',
            backgroundColor: 'rgba(0,0,0,0.6)',
            borderRadius: 20,
            marginBottom: 4
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: 8,
                height: 8,
                marginRight: 6,
                backgroundColor: getQualityColor(),
                borderRadius: '50%'
              }} />
              <span style={{
                color: 'white',
                fontSize: 12,
                fontWeight: 500
              }}>
                {getQualityText()}
              </span>
            </div>
          </div>
          <div style={{
            padding: '4px 12px',
            backgroundColor: 'rgba(0,0,0,0.6)',
            borderRadius: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {usingTwilioTurn ? (
                <Cloud size={12} color="#10b981" style={{ marginRight: 4 }} />
              ) : (
                <CloudOff size={12} color="#f59e0b" style={{ marginRight: 4 }} />
              )}
              <span style={{
                color: usingTwilioTurn ? '#10b981' : '#f59e0b',
                fontSize: 10,
                fontWeight: 500
              }}>
                {usingTwilioTurn ? 'Twilio' : 'Fallback'}
              </span>
            </div>
          </div>
        </div>

        {/* Connecting indicator */}
        {!isConnected && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 2
          }}>
            <div style={{
              padding: 24,
              backgroundColor: 'rgba(0,0,0,0.7)',
              borderRadius: 12
            }}>
              <div style={{
                width: 40,
                height: 40,
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: 16,
                margin: '0 auto 16px'
              }} />
              <p style={{
                color: 'white',
                fontSize: 16,
                fontWeight: 500,
                margin: 0
              }}>
                Connecting to {name}...
              </p>
            </div>
          </div>
        )}

        {/* Control buttons */}
        <div style={{
          position: 'absolute',
          bottom: 40,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-evenly',
          zIndex: 2
        }}>
          {/* Mic Button */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 60,
              height: 60,
              backgroundColor: isMicMuted ? '#dc3545' : 'rgba(255,255,255,0.15)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
            }} onClick={toggleMic}>
              {isMicMuted ? (
                <MicOff size={28} color="white" />
              ) : (
                <Mic size={28} color="white" />
              )}
            </div>
            <small style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: 12,
              fontWeight: 500,
              display: 'block',
              marginTop: 8
            }}>
              {isMicMuted ? 'Unmute' : 'Mute'}
            </small>
          </div>

          {/* Camera Button */}
          {!audioOnly && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 60,
                height: 60,
                backgroundColor:
                  callMode === CallMode.VIDEO
                    ? (isCameraOff ? '#dc3545' : 'rgba(255,255,255,0.15)')
                    : 'rgba(255,255,255,0.15)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
              }} onClick={toggleCamera}>
                {callMode === CallMode.VIDEO ? (
                  isCameraOff ? (
                    <VideoOff size={28} color="white" />
                  ) : (
                    <Video size={28} color="white" />
                  )
                ) : (
                  <Video size={28} color="white" />
                )}
              </div>
              <small style={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: 12,
                fontWeight: 500,
                display: 'block',
                marginTop: 8
              }}>
                {callMode === CallMode.VIDEO
                  ? (isCameraOff ? 'Camera On' : 'Camera Off')
                  : 'Video Call'
                }
              </small>
            </div>
          )}

          {/* End Call Button */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 60,
              height: 60,
              backgroundColor: '#dc3545',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
            }} onClick={endCall}>
              <PhoneOff size={28} color="white" />
            </div>
            <small style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: 12,
              fontWeight: 500,
              display: 'block',
              marginTop: 8
            }}>
              End Call
            </small>
          </div>

          {/* Speaker Button */}
          {/* <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 60,
              height: 60,
              backgroundColor: isSpeakerOn
                ? 'rgba(255,255,255,0.3)'
                : 'rgba(255,255,255,0.15)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
            }} onClick={toggleSpeaker}>
              {isSpeakerOn ? (
                <Volume2 size={28} color="white" />
              ) : (
                <VolumeX size={28} color="white" />
              )}
            </div>
            <small style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: 12,
              fontWeight: 500,
              display: 'block',
              marginTop: 8
            }}>
              {isSpeakerOn ? 'Speaker Off' : 'Speaker On'}
            </small>
          </div> */}
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(0.8); }
          100% { transform: scale(1.2); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default CallScreen;